import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  getSystemParticipantMatchesRequested,
  getSystemParticipantOffersRequested,
} from "../../../redux/actions/offers";
import {
  convertNumberToFrontend,
  convertAmountToFrontend,
} from "../../../helpers/convert";
// import DataTable from "../../../components/DataTable";
import { Input, Select } from "../../../components/Inputs";
import Button from "../../../components/Button";
import Loading from "../../../components/Loading/Loading";
import EmptyState from "../../../components/EmptyState/EmptyState";
import Pill from "../../../components/Pill";
import Radio from "../../../components/Radio/Radio";
import { contractTypes } from "../../../constants/selects";
import ModalCertificate from "../../../components/Modal/ModalCertificate";
import { exchangeOrder } from "../../../constants/translations";
import { setNotification } from "../../../helpers/notification";
import fileDownload from "js-file-download";
import { API_EXPORT } from "../../../helpers/api";
import DataTableServerSideRendering from "../../../components/DataTable/DataTableServerSideRendering";
import CanceledMatchModal from "../../../components/Modal/CanceledMatchModal";
import MarketResultSubDetailsModal from "../../../components/Modal/MarketResultSubDetailsModal";

export default function OffersAndMatches({ t }) {
  const dispatch = useDispatch();
  const organization = useSelector(
    (state) => state.authorization.user.organization
  );
  const { systemParticipantOffers, totalPageCount } = useSelector(
    (state) => state.offers.systemParticipantOffers
  );

  const systemParticipantMatches = useSelector(
    (state) => state.offers.systemParticipantMatches
  );

  const systemParticipantMatchesLoading = useSelector(
    (state) => state.offers.systemParticipantMatchesLoading
  );
  const actiPageRef = useRef(0);
  const [filter, setFilter] = useState({
    period: "",
    contractType: "-",
    requestType: "OFFERS",
  });
  const [inspectedCertificate, setInspectedCertificate] = useState();

  const [exportLoading, setExportLoading] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const [sortedColumn, setSortedColumn] = useState("productionPeriod");
  const [sortDirection, setSortDirection] = useState("desc");
  const translations = exchangeOrder(t);

  const offerStatusHandling = {
    CANCELLED: {
      color: "important",
      text: t("pages.default.offerOperations.status.cancelled"),
      showDelete: false,
    },
    OPEN: {
      color: "success",
      text: t("pages.default.offerOperations.status.open"),
      showDelete: true,
    },
    SOLD: {
      color: "primary",
      text: t("pages.default.offerOperations.status.sold"),
      showDelete: false,
    },
    DELETED: {
      color: "default",
      text: t("pages.default.offerOperations.status.deleted"),
      showDelete: false,
    },
    CLOSED: {
      color: "new",
      text: t("pages.default.offerOperations.status.closed"),
      showDelete: false,
    },
    DRAFT: {
      color: "attention",
      text: t("pages.default.offerOperations.status.draft"),
      showDelete: true,
    },
  };

  const offerCalculatedStatusHandler = {
    WAITING_MATCHES: t("pages.default.offerOperations.WAITING_MATCHES"),
    PARTIAL_MATCHES: t("pages.default.offerOperations.PARTIAL_MATCHES"),
    MATCHED: t("pages.default.offerOperations.MATCHED"),
    NON_MATCHES: t("pages.default.offerOperations.NON_MATCHES"),
  };

  const marketMatchesColumns = [
    {
      name: t("pages.default.offerOperations.table.contractName"),
      selector: "contractName",
      sortable: true,
    },
    {
      name: t("pages.default.offerOperations.table.matchDate"),
      selector: "matchDate",
      sortable: true,
      cell: (row) => {
        return moment(row.matchDate).format("DD.MM.YYYY HH:mm:ss");
      },
    },
    {
      name: t("pages.default.offerOperations.table.orderId"),
      selector: "orderId",
      sortable: true,
    },
    {
      name: t("pages.default.offerOperations.table.orderAmount"),
      selector: "quantity",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.quantity);
      },
    },
    {
      name: t("pages.default.offerOperations.table.orderPrice"),
      selector: "price",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.price, digit: 2 });
      },
    },
    {
      name: t("pages.default.offerOperations.table.matchAmount"),
      selector: "matchQuantity",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.matchQuantity);
      },
    },
    {
      name: t("pages.default.offerOperations.table.matchPrice"),
      selector: "matchPrice",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.matchPrice, digit: 2 });
      },
    },
    {
      name: t("pages.default.offerOperations.table.orderType"),
      selector: "orderType",
      sortable: true,
      cell: (row) => {
        return (
          <span
            className={
              row.orderType === "BUY"
                ? "km__u--text-green-300"
                : "km__u--text-red-300"
            }
          >
            {row.orderType === "BUY"
              ? t("pages.default.offerOperations.buy")
              : t("pages.default.offerOperations.sell")}
          </span>
        );
      },
    },
    {
      name: t("pages.default.marketResults.table.status"),
      selector: "status",
      sortable: true,
      cell: (row) => {
        return row.status.value === "VALID" ? (
          <Pill
            label={t("pages.default.organisationRegistration.active")}
            type="success"
          />
        ) : (
          <Pill
            label={t("pages.default.organisationRegistration.canceled")}
            type="important"
          />
        );
      },
    },
    {
      key: "inspect",
      name: t("pages.default.marketResults.table.inspect"),
      selector: "orderId",
      cell: (row) => {
        return (
          <Button
            title={t("ui.default.review")}
            suffixIcon="search"
            onClick={() => {
              setInspectedCertificate(row);
            }}
          />
        );
      },
    },
  ];

  const systemParticipantOffersColumns = [
    {
      name: t("pages.default.offerOperations.table.orderId"),
      selector: "shownId",
      sortable: true,
    },
    {
      name: t("pages.default.offerOperations.table.date"),
      selector: "date",
      sortable: true,
    },
    {
      name: t("pages.default.offerOperations.table.type"),
      selector: "type",
      sortable: true,
    },
    {
      name: t("pages.default.offerOperations.table.orderAmount"),
      selector: "amount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.amount);
      },
    },
    {
      name: t("pages.default.offerOperations.table.orderPrice"),
      selector: "price",
      sortable: true,
      cell: (row) => {
        return convertNumberToFrontend({ num: row.price, digit: 2 });
      },
    },
    {
      name: t("pages.default.offerOperations.table.remainingAmount"),
      selector: "remainingAmount",
      sortable: true,
      cell: (row) => {
        return convertAmountToFrontend(row.remainingAmount);
      },
    },
    {
      name: t("pages.default.offerOperations.table.orderType"),
      selector: "orderType",
      sortable: true,
      cell: (row) => {
        return (
          <span
            className={
              row.orderType === "BUY"
                ? "km__u--text-green-300"
                : "km__u--text-red-300"
            }
          >
            {row.orderType === "BUY"
              ? t("pages.default.offerOperations.buy")
              : t("pages.default.offerOperations.sell")}
          </span>
        );
      },
    },
    {
      name: t("pages.default.offerOperations.table.status"),
      selector: "status",
      sortable: true,
      cell: (row) => {
        return (
          <Pill
            type={
              offerStatusHandling[row.status]
                ? offerStatusHandling[row.status].color
                : ""
            }
            label={
              offerStatusHandling[row.status]
                ? offerStatusHandling[row.status].text
                : ""
            }
            emphasis
          />
        );
      },
    },
    {
      name: t("pages.default.offerOperations.orderStatus"),
      selector: "offerStatus",
      sortable: true,
      cell: (row) => {
        return offerCalculatedStatusHandler[row.offerStatus];
      },
    },
    {
      name: t("pages.default.offerOperations.reason"),
      selector: "reason",
      sortable: true,
      cell: (row) => {
        return translations[row.reason];
      },
    },
  ];

  // initial load of matches
  useEffect(() => {
    getMatches();
    getOffers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      getMatches();
      getOffers();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const filterRequest = () => {
    getMatches();
    getOffers();
  };

  const getMatches = () => {
    dispatch(
      getSystemParticipantMatchesRequested({
        organizationId: organization.organizationId,
        period: filter.period,
        contractType: filter.contractType,
        isMarketResult: false,
        noPage: false,
        page: actiPageRef.current,
      })
    );
  };

  const getOffers = () => {
    dispatch(
      getSystemParticipantOffersRequested({
        organizationId: organization.organizationId,
        period: filter.period,
        contractType: filter.contractType,
      })
    );
  };

  const filterChange = (e) => {
    setFilter({
      ...filter,
      [e.target.name]: e.target.value,
    });
  };

  const downloadOffersOrMatches = async () => {
    setExportLoading(true);
    try {
      const organizationParam =
        filter.requestType === "OFFERS"
          ? { organizationId: organization.organizationId }
          : { buyOrSellOrganizationId: organization.organizationId };
      const res = await API_EXPORT(
        filter.requestType === "OFFERS"
          ? "exchange-order/export/excel"
          : "exchange-order-match/export/excel",
        {
          ...organizationParam,
          sessionDate: filter.period ? filter.period.replace("-", "") : "",
          contractType:
            filter.contractType && filter.contractType !== "-"
              ? filter.contractType
              : "",
        }
      );
      fileDownload(
        res.data,
        filter.requestType === "OFFERS" ? "offers.xlsx" : "matches.xlsx"
      );
      setExportLoading(false);
      setNotification("success", t("ui.default.downloaded"));
    } catch (err) {
      err.response && setNotification("error", err.response.data.message);
      setExportLoading(false);
    }
  };
  const systemParticipantHandleClick = (activePage, selector, direction) => {
    console.log("page", activePage, selector, direction);
    setActivePage(activePage);

    dispatch(
      getSystemParticipantOffersRequested({
        organizationId: organization.organizationId,
        period: filter.period,
        contractType: filter.contractType,
        page: activePage,
        selector,
        direction,
      })
    );
  };
  const systemParticipantMatchesHandleClick = (
    activePage,
    selector,
    direction
  ) => {
    setActivePage(activePage);
    dispatch(
      getSystemParticipantMatchesRequested({
        organizationId: organization.organizationId,
        period: filter.period,
        contractType: filter.contractType,
        isMarketResult: false,
        noPage: false,
        page: activePage,
        selector,
        direction,
      })
    );
    actiPageRef.current = activePage;
  };
  return (
    <>
      <h5 className="km__u--text-has-emphasis">
        {t("pages.default.offerOperations.offersAndMatches")}
      </h5>
      <div className="km__p--card">
        <div className="km__p--market-results-contracts-header">
          <p className="km__u--text_title">
            {t("pages.default.offerOperations.offersAndMatchesList")}
          </p>
          <div className="km__p--market-results-contracts-header-filter">
            <Input
              type="month"
              name="period"
              placeholder={t("pages.default.marketResults.sessionPeriod")}
              value={filter.period}
              onChange={filterChange}
            />
            <Select
              options={contractTypes(t)}
              name="contractType"
              onChange={filterChange}
            />
            <Radio
              label={t("pages.default.offerOperations.myOffers")}
              name="requestType"
              value="OFFERS"
              onChange={filterChange}
              defaultChecked={filter.requestType === "OFFERS"}
              style={{ marginRight: "15px" }}
            />
            <Radio
              label={t("pages.default.offerOperations.myMatches")}
              name="requestType"
              value="MATCHES"
              defaultChecked={filter.requestType === "MATCHES"}
              onChange={filterChange}
              style={{ marginRight: "15px" }}
            />
            <Button
              label={t("ui.default.download")}
              suffixIcon="download"
              type="secondary"
              onClick={downloadOffersOrMatches}
              disabled={exportLoading}
            />
            <Button label={t("ui.default.filter")} onClick={filterRequest} />
          </div>
        </div>
        {filter.requestType === "MATCHES" && (
          // <DataTable
          //   rowKey="id"
          //   pagination
          //   paginationPerPage={10}
          //   columns={marketMatchesColumns}
          //   data={systemParticipantMatches?.systemParticipantMatches}
          // />
          <DataTableServerSideRendering
            columns={marketMatchesColumns}
            data={systemParticipantMatches?.systemParticipantMatches}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={systemParticipantMatches?.totalPageCount}
            handleFetchData={systemParticipantMatchesHandleClick}
          />
        )}
        {filter.requestType === "OFFERS" && (
          // <DataTable
          //   rowKey="id"
          //   pagination
          //   paginationPerPage={10}
          //   columns={systemParticipantOffersColumns}
          //   data={systemParticipantOffers}
          // />
          <DataTableServerSideRendering
            columns={systemParticipantOffersColumns}
            data={systemParticipantOffers}
            rowKey="id"
            sortedColumn={sortedColumn}
            setSortedColumn={setSortedColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            pagination
            activePage={activePage}
            totalPageCount={totalPageCount}
            handleFetchData={systemParticipantHandleClick}
          />
        )}
        {filter.requestType === "OFFERS" &&
          !systemParticipantMatchesLoading &&
          systemParticipantOffers &&
          systemParticipantOffers.length === 0 && (
            <EmptyState
              icon="file-text"
              text={t("pages.default.offerOperations.offersNotFound")}
            />
          )}
        {filter.requestType === "OFFERS" && systemParticipantMatchesLoading && (
          <Loading text={t("pages.default.offerOperations.offersLoading")} />
        )}
        {filter.requestType === "MATCHES" &&
          !systemParticipantMatchesLoading &&
          !systemParticipantMatches &&
          systemParticipantMatches.length === 0 && (
            <EmptyState
              icon="file-text"
              text={t("pages.default.offerOperations.matchesNotFound")}
            />
          )}
        {filter.requestType === "MATCHES" &&
          systemParticipantMatchesLoading && (
            <Loading text={t("pages.default.offerOperations.matchesLoading")} />
          )}
        {/* {inspectedCertificate?.status.value === "VALID" ? (
          <ModalCertificate
            t={t}
            certificateId={
              inspectedCertificate?.matchList[0]?.transferedCertificateId
            }
            isOpen={inspectedCertificate?.matchList[0] ? true : false}
            toggle={() => setInspectedCertificate()}
            startNo={inspectedCertificate?.matchList[0]?.documentStartNo}
            endNo={inspectedCertificate?.matchList[0]?.documentEndNo}
            amount={inspectedCertificate?.matchList[0]?.quantity}
            // requestUrl={`exchange-order-match/${inspectedCertificate.id}/`}
          />
        ) : (
          <CanceledMatchModal
            isOpen={inspectedCertificate ? true : false}
            toggle={() => setInspectedCertificate()}
            offerId={inspectedCertificate?.orderId}
            offerPrice={inspectedCertificate?.matchPrice}
            offerQuantity={inspectedCertificate?.matchQuantity}
            matchDate={inspectedCertificate?.matchDate}
            price={inspectedCertificate?.price}
            quantity={inspectedCertificate?.quantity}
            contractName={inspectedCertificate?.contractName}
            matchStatusDescription={
              inspectedCertificate?.matchStatusDescription
            }
          />
        )} */}
        {inspectedCertificate?.status?.value === "CANCELLED" ? (
          <CanceledMatchModal
            isOpen={inspectedCertificate ? true : false}
            toggle={() => setInspectedCertificate()}
            offerId={inspectedCertificate?.orderId}
            offerPrice={inspectedCertificate?.matchPrice}
            offerQuantity={inspectedCertificate?.quantity}
            matchDate={inspectedCertificate?.matchDate}
            price={inspectedCertificate?.price}
            quantity={inspectedCertificate?.matchAmount}
            contractName={inspectedCertificate?.contractName}
            matchStatusDescription={
              inspectedCertificate?.matchStatusDescription
            }
          />
        ) : (
          <>
            {inspectedCertificate &&
            inspectedCertificate?.matchList?.length === 1 ? (
              <ModalCertificate
                isOpen={inspectedCertificate?.matchList[0] ? true : false}
                toggle={() => setInspectedCertificate()}
                certificateId={
                  inspectedCertificate?.matchList[0]?.transferedCertificateId
                }
                startNo={inspectedCertificate?.matchList[0]?.documentStartNo}
                endNo={inspectedCertificate?.matchList[0]?.documentEndNo}
                amount={inspectedCertificate?.matchList[0]?.quantity}
              />
            ) : (
              <MarketResultSubDetailsModal
                t={t}
                // isOpen={bilateralAgreementDetailsModal}
                // toggle={() =>
                //   setBilateralAgreementDetailsModal(!bilateralAgreementDetailsModal)
                // }
                datas={inspectedCertificate}
                isOpen={inspectedCertificate?.matchList ? true : false}
                toggle={() => setInspectedCertificate()}
                certificateId={
                  inspectedCertificate?.matchList[0]?.transferedCertificateId
                }
                // selectedAgreement={selectedBilateralAgreement}
                // cancelMode={cancelMode}
                // cancelOnclick={updateBilateralAgreementStatus}
                // loading={loadingRequest}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
